/**
 * FauxDS PasswordInput
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate PasswordInput component for prototyping.
 * It is NOT the real @faire/slate PasswordInput and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate PasswordInput, see: @faire/slate/components/password-input
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useId, useState, useCallback } from "react";

export interface PasswordInputProps {
  /**
   * The label of the password input
   */
  label?: string;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Current value (controlled)
   */
  value?: string;
  /**
   * Default value (uncontrolled)
   */
  defaultValue?: string;
  /**
   * Change handler
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Whether the input is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Error state - can be boolean or error message string
   */
  error?: boolean | string;
  /**
   * Helper text displayed below the input
   */
  helper?: string;
  /**
   * Input name attribute
   */
  name?: string;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * Whether to show password by default
   * @default false
   */
  defaultShowPassword?: boolean;
}

// Eye icon (show password)
const EyeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M10 4C4 4 1 10 1 10s3 6 9 6 9-6 9-6-3-6-9-6Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="10" r="3" />
  </svg>
);

// Eye-off icon (hide password)
const EyeOffIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M10 4C4 4 1 10 1 10s3 6 9 6 9-6 9-6-3-6-9-6Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="10" r="3" />
    <path d="M3 17L17 3" strokeLinecap="round" />
  </svg>
);

/**
 * PasswordInput - FauxDS (recreated from Slate)
 *
 * A Password Input allows users to enter and edit their passwords.
 * This is a simplified implementation for prototyping purposes.
 */
const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label,
      placeholder,
      value,
      defaultValue,
      onChange,
      disabled = false,
      error,
      helper,
      name,
      className = "",
      defaultShowPassword = false,
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = `password-${generatedId}`;
    const helperId = `helper-${generatedId}`;

    const [showPassword, setShowPassword] = useState(defaultShowPassword);

    const togglePasswordVisibility = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setShowPassword((v) => !v);
    }, []);

    const hasError = !!error;
    const errorMessage = typeof error === "string" ? error : null;
    const helperText = errorMessage || helper;

    return (
      <div className={cn("flex flex-col", className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "mb-[8px] font-sans text-[14px] leading-[20px]",
              disabled ? "text-[#757575]" : "text-fs-text-primary",
              hasError ? "text-[#921100]" : ""
            )}
          >
            {label}
          </label>
        )}

        {/* Input container */}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={showPassword ? "text" : "password"}
            name={name}
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={hasError}
            aria-describedby={helperText ? helperId : undefined}
            className={cn(
              "text-fs-text-primary bg-fs-surface-primary h-[40px] w-full rounded-[4px] border px-[16px] pr-[44px] font-sans text-[14px] leading-[20px] transition-all duration-150 ease-in-out placeholder:text-[#757575]",
              hasError ? "border-[#d17e70]" : "border-[#dfe0e1]",
              disabled
                ? "cursor-not-allowed border-[#dfe0e1] bg-[#f7f7f7] text-[#757575]"
                : "",
              "focus:text-[#000000] focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-[#000000]"
            )}
          />

          {/* Toggle visibility button */}
          <button
            type="button"
            onClick={togglePasswordVisibility}
            disabled={disabled}
            className={cn(
              "text-fs-text-primary absolute top-1/2 right-[12px] flex -translate-y-1/2 items-center justify-center rounded-sm p-1 hover:text-[#000000] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[#275ec5]",
              disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
            )}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>

        {/* Helper/Error Text */}
        {helperText && (
          <span
            id={helperId}
            className={cn(
              "mt-[4px] font-sans text-[12px] leading-[16px]",
              hasError ? "text-[#921100]" : "text-[#757575]"
            )}
          >
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
